import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, debounceTime, filter, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { JobseekerSettingsService } from 'src/app/jobseeker/shared/services/jobseeker-settings.service';
import * as JobseekerSettingsActions from './actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../state';
import { displaySnackBarMessage } from 'src/app/root-store/actions';
import { createErrorMessage, createSuccessMessage, CustomSnackbarService } from '@hrra/ui';
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { VerificationDialogComponent } from 'src/app/jobseeker/jobseeker-update-email-dialog/jobseeker-update-email.component';
import { CountdownService } from 'src/app/jobseeker/shared/services/countdown.service';
import { startVerificationTimer, stopVerificationTimer } from './actions';
import { HRRAExceptionCode } from '@hrra/common-models';


@Injectable()
export class JobseekerSettingsEffects{

    getJobseekerPrivacy$ = createEffect(() => this.actions$.pipe(
        ofType(JobseekerSettingsActions.loadJobseekerPrivacy),
        mergeMap(() => this.jobseekerSettingsService.getJobseekerPrivacy()
        .pipe(
            map(response => JobseekerSettingsActions.loadJobseekerPrivacySuccess({payload: response.data})),
            catchError(response => of(JobseekerSettingsActions.loadJobseekerPrivacyFail({payload: response.error})))
        ))
    ));

    updateJobseekerPrivacy$ = createEffect(() => this.actions$.pipe(
        ofType(JobseekerSettingsActions.updateJobseekerPrivacy),
        mergeMap((action) => this.jobseekerSettingsService.updateJobseekerPrivacy(action.payload.privacyId)
        .pipe(
            map(response => JobseekerSettingsActions.updateJobseekerPrivacySuccess({payload: response.data})),
            catchError(response => of(JobseekerSettingsActions.updateJobseekerPrivacyFail({payload: response.error})))
        ))
    ));

    updateJobseekerPrivacySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(JobseekerSettingsActions.updateJobseekerPrivacySuccess),
        tap(() => this.snackBarService.open(this.translate.instant('message.SaveMessage'), null, { duration: 2000, panelClass: ['snackbar-success']})
        )
    ), { dispatch: false });

  changeJobseekerPassword$ = createEffect(() => this.actions$.pipe(
    ofType(JobseekerSettingsActions.changeJobseekerPassword),
    mergeMap((action) => this.jobseekerSettingsService.changeJobseekerPassword(action.payload)
      .pipe(
        map(() => JobseekerSettingsActions.changeJobseekerPasswordSuccess()),
        catchError(response => of(JobseekerSettingsActions.changeJobseekerPasswordFail({ payload: response.error })))
      ))
  ));

  changeJobseekerPasswordSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(JobseekerSettingsActions.changeJobseekerPasswordSuccess),
    tap(() => this.customSnackBarService.openSnackbar(createSuccessMessage())
    )
  ), { dispatch: false });

  loadJobseekerBlacklist$ = createEffect(() => this.actions$.pipe(
    ofType(JobseekerSettingsActions.loadJobseekerBlacklist),
    debounceTime(500),
    mergeMap((action) => this.jobseekerSettingsService.getJobseekerBlacklist(action.payload.name)
      .pipe(
        map(response => JobseekerSettingsActions.loadJobseekerBlackListSuccess({ payload: response.data })),
        catchError(response => of(JobseekerSettingsActions.loadJobseekerBlacklistFail({ payload: response.error })))
      ))
  ));

  updateJobseekerBlacklist$ = createEffect(() => this.actions$.pipe(
    ofType(JobseekerSettingsActions.updateJobseekerBlacklist),
    mergeMap((action) => this.jobseekerSettingsService.updateJobseekerBlacklist(action.payload)
      .pipe(
        map(action => JobseekerSettingsActions.updateJobseekerBlacklistSuccess({payload: action.data})),
        catchError(response => of(JobseekerSettingsActions.updateJobseekerBlacklistFail({ payload: response.error })))
      ))
   ));

  updateJobseekerBlacklistSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(JobseekerSettingsActions.updateJobseekerBlacklistSuccess),
    tap(() => this.store.dispatch(displaySnackBarMessage({payload: createSuccessMessage()})))
      ),
    { dispatch: false }
  );

  updateJobseekerBlacklistFail$ = createEffect(() => this.actions$.pipe(
    ofType(JobseekerSettingsActions.updateJobseekerBlacklistFail),
    tap(() => this.store.dispatch(displaySnackBarMessage({payload: createErrorMessage()})))
      ),
    { dispatch: false }
  );

  
  updateEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobseekerSettingsActions.updateEmail),
      mergeMap((action) =>
        this.jobseekerSettingsService.updateEmail(action.payload.newEmail, action.payload.password).pipe(
          map(() => JobseekerSettingsActions.updateEmailSuccess({ newEmail: action.payload.newEmail, password: action.payload.password })),
          catchError((error) => of(JobseekerSettingsActions.updateEmailFailure({ error })))
        )
      )
    )
  );
    
    updateEmailSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(JobseekerSettingsActions.updateEmailSuccess),
        mergeMap(({ newEmail, password }) => {

          this.dialog.open(VerificationDialogComponent, { data: { newEmail, password }, id: 'updateEmail', width: '51.8125rem', maxWidth: '51.8125rem', height: 'fit-content', position: { top: '6.88rem' } });
    
          return [JobseekerSettingsActions.requestVerificationCode({ newEmail, password })];
        })
      )
    );

    updateUserEmail$ = createEffect(() => 
      this.actions$.pipe(
        ofType(JobseekerSettingsActions.updateUserEmail),
        mergeMap((action) => this.jobseekerSettingsService.confirmUserEmail(action.payload).pipe(
          map(() => JobseekerSettingsActions.updateUserEmailSuccess({payload: {newEmail: action.payload.newEmail}})),
          catchError((error) => of(JobseekerSettingsActions.updateUserEmailFail({error})))
        ))
      )
    )

    requestVerificationCode$ = createEffect(() =>
      this.actions$.pipe(
        ofType(JobseekerSettingsActions.requestVerificationCode),
        mergeMap(({ newEmail, password }) =>
          this.jobseekerSettingsService.requestVerificationCode(newEmail, password).pipe(
            map((res) => JobseekerSettingsActions.requestVerificationCodeSuccess({payload: res.data})),
            catchError((error) => of(JobseekerSettingsActions.requestVerificationCodeFail({ error })))
          )
        )
      )
    );

    requestVerificationCodeFailToast$ = createEffect(() =>
      this.actions$.pipe(
        ofType(JobseekerSettingsActions.requestVerificationCodeFail),
        filter(({ error }) => error?.error?.errorCode === HRRAExceptionCode.OTPRateLimitExceeded),
        tap(({ error }) => {
          const seconds = this.toRemainingSeconds(error?.error?.additionalInfo?.nextRequestAllowedDate);
          const text = this.translate.instant(
            'message.ResendVerificationNotAllowedYet',
            { seconds }
          );
          this.customSnackBarService.openSnackbar(createErrorMessage(text, '', 4000));
        })
      ),
      { dispatch: false }
    );

    private toRemainingSeconds(dateStr: string | null | undefined): number {
      if (!dateStr) return 0;
      const normalized = /[Zz]|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z';
      const remainingMs = new Date(normalized).getTime() - Date.now();
      return Math.max(0, Math.ceil(remainingMs / 1000));
    }

    private readonly otpErrorTranslationKeys: Record<number, string> = {
      [HRRAExceptionCode.OTPInvalidRequest]: 'message.OTPInvalidRequest',
      [HRRAExceptionCode.OTPSendingFailed]: 'message.OTPSendingFailed',
      [HRRAExceptionCode.OTPInternalServiceError]: 'message.OTPInternalServiceError',
      [HRRAExceptionCode.OTPCodeAttemptLimitExcceded]: 'message.OTPCodeAttemptLimitExceeded',
      [HRRAExceptionCode.OTPIvalidConfigurationError]: 'message.OTPInvalidConfigurationError',
      [HRRAExceptionCode.OTPTokenNotFound]: 'message.OTPTokenNotFound',
      [HRRAExceptionCode.OTPCodeUsed]: 'message.OTPCodeUsed',
      [HRRAExceptionCode.OTPRequestIdMismatch]: 'message.OTPRequestIdMismatch',
    };

    private showOtpErrorToast(errorCode: number): void {
      const key = this.otpErrorTranslationKeys[errorCode];
      if (!key) return;
      const text = this.translate.instant(key);
      this.customSnackBarService.openSnackbar(createErrorMessage(text, '', 4000));
    }

    requestVerificationCodeOtpErrorToast$ = createEffect(() =>
      this.actions$.pipe(
        ofType(JobseekerSettingsActions.requestVerificationCodeFail),
        map(({ error }) => error?.error?.errorCode as number),
        filter(code => code != null && this.otpErrorTranslationKeys[code] !== undefined),
        tap(code => this.showOtpErrorToast(code))
      ),
      { dispatch: false }
    );

    updateUserEmailOtpErrorToast$ = createEffect(() =>
      this.actions$.pipe(
        ofType(JobseekerSettingsActions.updateUserEmailFail),
        map(({ error }) => error?.error?.errorCode as number),
        filter(code => code != null && this.otpErrorTranslationKeys[code] !== undefined),
        tap(code => this.showOtpErrorToast(code))
      ),
      { dispatch: false }
    );

    private readonly otpTerminalValidationCodes = new Set<number>([
      HRRAExceptionCode.OTPCodeExpired,
      HRRAExceptionCode.OTPCodeAttemptLimitExcceded,
      HRRAExceptionCode.OTPCodeUsed,
      HRRAExceptionCode.OTPRequestIdMismatch,
      HRRAExceptionCode.OTPTokenNotFound,
    ]);

    stopVerificationTimerOnTerminalOtpError$ = createEffect(() =>
      this.actions$.pipe(
        ofType(JobseekerSettingsActions.updateUserEmailFail),
        map(({ error }) => error?.error?.errorCode as number),
        filter(code => code != null && this.otpTerminalValidationCodes.has(code)),
        map(() => stopVerificationTimer())
      )
    );

    triggerTimer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobseekerSettingsActions.requestVerificationCodeSuccess),
      map(() => startVerificationTimer())
     )
    );

    runTimer$ = createEffect(() =>
      this.actions$.pipe(
        ofType(JobseekerSettingsActions.startVerificationTimer),
        switchMap(() =>
          this.countdownService.start(5).pipe(
            map((seconds) =>
              seconds > 0
                ? JobseekerSettingsActions.timerTick({ seconds })
                : JobseekerSettingsActions.timerFinished()
            ),
            takeUntil(
            this.actions$.pipe(ofType(stopVerificationTimer))
            )
          )
        )
      )
    );
    
    constructor(private store: Store<AppState>, 
      private actions$: Actions, 
      private countdownService: CountdownService,
      private jobseekerSettingsService: JobseekerSettingsService, 
      private dialog: MatDialog,
      private snackBarService: MatSnackBar, 
      private customSnackBarService: CustomSnackbarService,
      private translate: TranslateService){}
}